<template>
  <TemplateModal
    v-bind="$attrs"
    :show-close="false"
    width="30%"
    :before-close="() => emit('update:modelValue', false)"
    class="exportar-relatorio-dre-dialog"
  >
    <template #header>
      <div class="exportar-relatorio-dre-dialog__header">
        <h3>Exportar Relatório DRE</h3>
        <MaterialIcon
          class="close"
          icon="close"
          cursor="pointer"
          @click="handleCancelar"
          size="20px"
        />
      </div>
    </template>
    <template #content>
      <div class="exportar-relatorio-dre-dialog__content">
        <F360Checkbox
          v-model="form.emailInfo.enviarEmail"
          label="Enviar relatório para o e-mail"
        />
        <F360Input
          v-model="form.emailInfo.userEmail"
          :error="disableSave"
          clearable
          placeholder="Digite um e-mail válido"
          type="email"
        />
      </div>
    </template>
    <template #footer>
      <div class="exportar-relatorio-dre-dialog__footer">
        <F360Button @click="handleCancelar">Cancelar</F360Button>
        <F360Button
          filled
          :disabled="disableSave"
          @click="handleExportarDadosDoDRE"
          >Exportar</F360Button
        >
      </div>
    </template>
  </TemplateModal>
</template>

<script setup lang="ts">
import F360Button from "@/ui/components/Buttons/Button/F360Button.vue";
import {
  IFinancasFeatureGateway,
  TExportarDadosDoDREPayload
} from "../../services/ports/FinancasFeatureGateway";
import { DIMixins, DITypes } from "@/core/@types/types";
import { diContainer } from "@/inversify.config";
import TemplateModal from "@/ui/components/Dialogs/Template/TemplateModal.vue";
import F360Checkbox from "@/ui/components/Checkboxs/Checkbox/F360Checkbox.vue";
import F360Input from "@/ui/components/Inputs/Input/F360Input.vue";
import { computed, reactive } from "vue";
import { IUserProfileStorageMixin } from "@/core/plugins/storage/UserProfileStorageMixin";
import MaterialIcon from "@/ui/components/Icons/MaterialIcon/MaterialIcon.vue";
import useNotification from "@/core/composables/notification/useNotification";
import { useExportarRelatorioStore } from "./useExportarRelatorioStore";

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const notification = useNotification();

const financasGateway = diContainer.get<IFinancasFeatureGateway>(
  DITypes.FINANCAS_FEATURES
);

const profile = diContainer
  .get<IUserProfileStorageMixin>(DIMixins.USER_PROFILE_STORAGE)
  .handleGetUserProfile();

const exportarRelatorioStore = useExportarRelatorioStore();

const form = reactive<TExportarDadosDoDREPayload>({
  emailInfo: {
    enviarEmail: true,
    userEmail: profile?.Email || ""
  },
  pesquisa: {
    CompetenciaDe: exportarRelatorioStore.competenciaDe || "",
    CompetenciaAte: exportarRelatorioStore.competenciaAte || "",
    RegimeDeCompetencia: exportarRelatorioStore.regimeDeCompetencia || true,
    Modelo: exportarRelatorioStore.modelo || ""
  }
});

const disableSave = computed(() => {
  return form.emailInfo.enviarEmail && !form.emailInfo.userEmail;
});

const handleExportarDadosDoDRE = async () => {
  if (disableSave.value) return;

  const response = await financasGateway.exportarDadosDoDRE({
    emailInfo: {
      enviarEmail: form.emailInfo.enviarEmail,
      userEmail: form.emailInfo.userEmail
    },
    pesquisa: {
      CompetenciaDe: exportarRelatorioStore.competenciaDe || "",
      CompetenciaAte: exportarRelatorioStore.competenciaAte || "",
      RegimeDeCompetencia: exportarRelatorioStore.regimeDeCompetencia || true,
      Modelo: exportarRelatorioStore.modelo || ""
    }
  });

  if (response?.Ok) {
    const emailSuffix = form.emailInfo.enviarEmail
      ? ` ${form.emailInfo.userEmail}`
      : " para seu e-mail";

    const msg = `O seu relatório está sendo processado em background e quando estiver finalizado enviaremos um e-mail${emailSuffix}.<br><br>Você poderá acompanhar o status da exportação na <a href="/Relatorio" target="_blank" rel="noopener">tela de gestão de relatórios</a>.`;

    notification.showHtmlNotification(msg, {
      type: "success",
      timeout: null
    });

    handleCancelar();
  } else {
    notification.showNotification(
      response?.Error || "Erro ao exportar relatório",
      {
        type: "error"
      }
    );
  }
};

const handleCancelar = () => {
  emit("update:modelValue", false);
};
</script>

<style scoped lang="scss">
.exportar-relatorio-dre-dialog {
  .exportar-relatorio-dre-dialog__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    width: 100%;

    h3 {
      margin: 0;
      font-size: 14px;
      font-weight: 700;
      line-height: 1.25rem;
      color: _map.get($map: $neutro, $key: 900);
    }

    .close:hover {
      color: _map.get($map: $neutro, $key: 700);
    }
  }
  .exportar-relatorio-dre-dialog__close {
    cursor: pointer;
  }
  .exportar-relatorio-dre-dialog__content {
    display: flex;
    flex-direction: column;
    gap: _map.get($map: $padding, $key: m);
  }
  .exportar-relatorio-dre-dialog__footer {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding: 0;
  }
}
</style>
